import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from 'react-oidc-context'
import { useCurrentUser } from '../../auth/useCurrentUser'
import { readActingUserId } from '../../auth/actingUser'
import { getProxyPersonas, proxyPersonaKeys, type ProxyPersona } from './api/proxyPersonaApi'
import { useProxySwitch } from './useProxySwitch'

/**
 * <b>Kendi profili olmayan asistanı, açılışta yöneticinin kipine kendiliğinden sokar</b>
 * (kullanıcı isteği 21.10.2026).
 *
 * <p><b>Çözdüğü sorun ölçüldü:</b> asistan İK'dan seçiliyor ve sisteme girebilmesi için bir role
 * ihtiyacı yok — 099 erişim kapısı onu <i>yalnız asistan kaydı olduğu için</i> içeri alıyor. Böyle
 * bir kullanıcının kendi yetki kümesi <b>boştur</b>; {@code NavMenu} her maddeyi
 * {@code hasAnyGrant(...viewGrants)} ile süzdüğü için menü tamamen boş gelir. Yani kullanıcı giriş
 * yapıyor ve <b>bomboş bir uygulamaya</b> düşüyor; oysa yapabileceği iş yöneticisinin kipinde
 * duruyor.
 *
 * <p><b>Neden istemcide:</b> persona 089'da bilinçle istemci durumudur (çerez →
 * {@code X-Acting-User-Id}); sunucu yalnız doğrular. Varsayılanı sunucuya taşımak {@code /me}'nin
 * kimliği kullanıcı istemeden değiştirmesi olurdu ve 089'un tüm akışını bulanıklaştırırdı.
 *
 * <p><b>Koşullar — hepsi birden.</b> Biri bile sağlanmazsa hiçbir şey yapılmaz:
 * <ol>
 *   <li>{@code /me} geldi ve kullanıcı zaten bir kipte <b>değil</b>,
 *   <li>çerezde seçili persona <b>yok</b> (önceki seçim varsa ona dokunulmaz),
 *   <li>kullanıcının <b>hiç rolü yok</b> — yani kendi profili diye bir şey yok,
 *   <li>seçilebilir persona sayısı <b>tam olarak bir</b> — birden fazlaysa geçiş yapılmaz ama
 *       kullanıcı boş ekranda da bırakılmaz: çağıran bir <b>seçim ekranı</b> gösterir (bkz. dönüş
 *       değeri ve {@code AssistantPersonaChooser}).
 * </ol>
 *
 * <p><b>Her sayfa yüklenişinde denenir ve bu bilinçlidir.</b> İlk yazımda oturum başına bir kez
 * deneniyordu — gerekçe "kullanıcının bilinçli çıkışını geri alma"ydı. O gerekçe aynı turda
 * <b>ortadan kalktı</b>: "Kendim" satırı bu kullanıcı sınıfında artık hiç sunulmuyor (FR-017
 * istisnası), yani <b>bilinçli çıkış diye bir şey yok</b>. Bayrak kalınca yalnız kullanıcıyı boş
 * ekranda tutuyordu — kullanıcı bunu ekranda bildirdi (21.10.2026): persona listede duruyor ama
 * seçili gelmiyordu.
 *
 * <p>Sonsuz döngü riski yok: {@code started} ref'i aynı mount içinde ikinci denemeyi keser, geçiş
 * başarısız olursa kullanıcı normal kabuğa düşer ve menüden elle seçebilir.
 *
 * <p><b>Neden "tam olarak bir":</b> bir kişi birden çok yöneticinin asistanı olabilir (spec
 * Varsayımlar). Birden fazlasında hangisinin kastedildiği <b>bilinemez</b>; rastgele birini seçmek
 * kullanıcıyı yanlış kişinin verisiyle çalıştırmak olurdu ve Tam Yetkide o kişi, farkında olmadan
 * <b>yanlış yöneticinin adına</b> görev açıp düzenleyebilirdi.
 *
 * <p><b>Ama tahmin etmemek, boş ekrana düşürmek demek DEĞİL</b> (kullanıcı bildirimi 21.10.2026:
 * *"bir asistana iki yetki verildiği zaman yine kendi profili açık geliyor … sonuçta hâlâ kendi rolü
 * yoktu"*). İlk yazımda bu durumda hiçbir şey yapılmıyordu ve kullanıcı, menüden elle seçene kadar
 * <b>bomboş</b> bir uygulamaya düşüyordu — yani "Kendim"i gizlerken kaçınılan durumun ta kendisi.
 * Artık {@code 'choose'} dönülür ve çağıran bir seçim ekranı gösterir: tek tık, açık, yanlış veriye
 * düşme riski yok. Seçim çereze yazıldığı için sonraki yüklemelerde doğrudan o kiple açılır.
 *
 * <p><b>Neden rol sayısı, yetki sayısı değil:</b> ölçüt isteğin kendi ifadesidir
 * (<i>"hiçbir rolü yok ise"</i>). Yetki kümesi role bağlı olmayan kaynaklardan da dolabiliyor
 * (ör. kadro parametresinden türeyen {@code ASSISTANT_MANAGE}); o yüzden "yetkisi yok" ölçütü
 * yöneticinin kendisini de yakalayabilirdi.
 *
 * @returns {@code entering} — geçiş sürüyor, çağıran kabuk yerine yükleme göstermeli (kullanıcı boş
 *     menüyü bir an görmesin). {@code choose} — birden çok seçilebilir yönetici var, çağıran seçim
 *     ekranını göstermeli ve listeyi <b>buradan</b> almalı. {@code idle} — yapılacak bir şey yok.
 */
export type AssistantPersonaState =
  | { kind: 'idle' }
  | { kind: 'entering' }
  /**
   * Seçilebilir yöneticiler. Liste <b>burada</b> taşınıyor çünkü seçim ekranının kendi
   * {@code useQuery}'sini açması <b>ikinci bir GET</b> üretiyordu (push kapısı review'ü ölçtü: 2
   * istek). Sebebi önbelleğin boş olması değil — {@code staleTime: 0} + {@code refetchOnMount}
   * varsayılanları, yeni bir observer mount olunca aynı anahtarı yeniden çekiyor. Prop olarak
   * geçirmek ikinci observer'ı hiç açmıyor.
   */
  | { kind: 'choose'; personas: ProxyPersona[] }

export function useAssistantAutoPersona(): AssistantPersonaState {
  const auth = useAuth()
  const { data: me } = useCurrentUser()
  const { enterPersona } = useProxySwitch()
  const [entering, setEntering] = useState(false)
  // Geçiş bir kez başlatılır: `enterPersona` önbelleği geçersiz kılıyor ve yeniden render
  // tetikliyor, bayrak olmadan efekt kendini yeniden kurabilirdi.
  const started = useRef(false)

  // `roleIds` ALANI EKSİK gelirse (eski backend / rolling deploy penceresi) kullanıcı **rolsüz
  // SAYILMAZ**: `useCurrentUser` o alan için fail-open bir sözleşme yazıyor (*"okuyanlar `?? []`
  // ile korunur → hiçbir şey gizlenmez"*). `?? 0 === 0` yazsaydık alanı göndermeyen bir sunucuda
  // HERKES rolsüz sayılır ve tek personası olan normal kullanıcılar istemedikleri kimliğe
  // geçerdi. Bu yüzden ölçüt "dizi VAR ve BOŞ".
  const roleless = Array.isArray(me?.roleIds) && me.roleIds.length === 0
  const inPersona = me?.proxy?.active === true
  const eligible =
    auth.isAuthenticated && roleless && !inPersona && readActingUserId() == null

  // Liste YALNIZ koşullar sağlanınca çekilir: rolü olan kullanıcıda bu hook hiçbir istek açmaz.
  const { data: personas } = useQuery({
    queryKey: proxyPersonaKeys.list,
    enabled: eligible,
    queryFn: getProxyPersonas,
  })

  // `useMemo` ZORUNLU: dizi her render'da yeniden üretilseydi aşağıdaki efektin bağımlılığı her
  // seferinde değişir ve efekt boşuna yeniden kurulurdu (`started` ref'i eylemi engelliyor olsa da).
  const selectable = useMemo(() => personas?.filter((p) => p.selectable), [personas])

  useEffect(() => {
    if (!eligible || started.current || selectable == null) return
    if (selectable.length !== 1) return
    const target = selectable[0]
    if (target == null) return
    started.current = true
    setEntering(true)
    // Hata yutulmaz ama ekranı da kilitlemez: `enterPersona` kendi içinde çerezi yazmaz ve
    // bildirimi `useProxyPersonaMenu`'dekiyle aynı huniye düşmez, bu yüzden burada yalnız
    // yüklemeyi kapatıp kullanıcıyı normal (boş) kabuğa bırakırız — oradan elle seçebilir.
    enterPersona(target.userId)
      // Reddi YUTMAK bilinçli DEĞİL, bildirimi göstermemek bilinçli: `runSwitch` hatayı yeniden
      // fırlatıyor ve projede global bir `unhandledrejection` dinleyicisi yok, yani yakalanmazsa
      // konsola düşen sahipsiz bir red kalırdı. Kullanıcıya bildirim göstermiyoruz çünkü bu, onun
      // başlatmadığı bir işlem; başarısızlıkta normal (boş) kabuğa düşer ve menüden elle seçebilir.
      .catch(() => {})
      .finally(() => setEntering(false))
  }, [eligible, selectable, enterPersona])

  // `useMemo`: dönüş artık bir NESNE ve her render'da yenisini üretmek, bu durumu bir `useEffect`
  // bağımlılığına koyacak sonraki çağırana sonsuz döngü yazdırırdı (push kapısı review'ü). Bugünkü
  // tek tüketici yalnız `state.kind` okuyor, yani hata değil — tuzak.
  return useMemo<AssistantPersonaState>(() => {
    if (entering) return { kind: 'entering' }
    // Seçim ekranı YALNIZ liste indikten sonra: `selectable` henüz `undefined` iken 'choose' dönmek,
    // sorgu çözülene kadar boş bir seçim ekranı çizdirirdi.
    if (eligible && selectable != null && selectable.length > 1) {
      return { kind: 'choose', personas: selectable }
    }
    return { kind: 'idle' }
  }, [entering, eligible, selectable])
}
